import React, { useEffect, useRef, useState, FC } from 'react';
import { Placeholder, ToastContainer } from '@goodrx/matisse';
import { useSynapse } from 'app/lib/synaptik';
import { BrowserRouter, Route, Redirect, Switch, withRouter } from 'react-router-dom';
import * as Pages from 'app/routes';
import { trackEvent, trackScreen } from 'app/lib/analytics';
import ChatDrawer from 'app/components/widgets/chat/chat-drawer';
import { withOptimizely } from 'app/components/withOptimizely';
import { AppLayout } from 'app/components/AppLayout/AppLayout';
import { Location } from 'history';
import { UnauthenticatedRoute } from './UnauthenticatedRoute';
import { AuthenticatedRoute } from './AuthenticatedRoute';

const getLocationPath = (location: Location) => location.pathname + location.search;

const PageTracker = withRouter(({ history, location }) => {
  const lastPath = useRef(getLocationPath(location));
  useEffect(() => {
    trackScreen();
    return history.listen((updatedLocation) => {
      const newPath = getLocationPath(updatedLocation);
      if (lastPath.current !== newPath) {
        trackScreen();
        lastPath.current = newPath;

        window.scrollTo(0, 0);
      }
    });
  }, []);
  return null;
});

function ChatRoute({ history }: any) {
  const [toggleChat] = useSynapse(({ chat }) => [chat.toggleChat]);
  useEffect(() => {
    toggleChat();
    history.replace('/account/start');
  }, []);
  return null;
}

function GoldApp() {
  return (
    <Switch>
      <Route path="/gold/chat" exact component={Pages.Chat} />
      <Route path="/gold/service/:id" exact component={Pages.Service} />
      <Route path="/gold/drug-information" exact component={Pages.GoldDrugInformation} />
    </Switch>
  );
}

function HeyDoctorApp() {
  return (
    <AppLayout>
      <PageTracker />
      <Switch>
        <Route path="/verify-email/:token" component={Pages.Auth} />
        <Route exact path="/start/:slug" component={Pages.Start} />
        <Route path="/coupon" component={Pages.Coupon} />
        <Route path="/drug-information" component={Pages.DrugInformation} />
        <Route path="/education/:id" component={Pages.Education} />

        <UnauthenticatedRoute path="/login" component={Pages.Auth} />
        <UnauthenticatedRoute path="/register" component={Pages.Auth} />
        <UnauthenticatedRoute path="/forgot-password" component={Pages.Auth} />
        <UnauthenticatedRoute path="/reset-password" component={Pages.Auth} />

        <AuthenticatedRoute path="/start/:slug/triage" exact component={Pages.Triage} />
        <AuthenticatedRoute path={['/messages', '/account/messages']} component={ChatRoute} />
        <AuthenticatedRoute path="/account" component={Pages.Account} />
        <AuthenticatedRoute path={['/visits/:id', '/visits/:id/:slug']} exact component={Pages.Visit} />

        <Redirect from="*" to="/login" />
      </Switch>

      <ChatDrawer />
      <ToastContainer />
    </AppLayout>
  );
}

/**
 * Main application router
 * all pages should be imported via NextJs Dynamic Import
 * @see https://nextjs.org/docs/advanced-features/dynamic-import
 */

const Router: FC = () => {
  const [bootstrapped, setBootstrapped] = useState(false);

  const [fetchServices, fetchAvailableStates] = useSynapse(({ services, business }) => {
    return [services.list, business.fetchAvailableStates];
  });

  useEffect(() => {
    const bootstrap = async () => {
      await Promise.all([fetchAvailableStates(), fetchServices()]);

      trackEvent('Web App Loaded');

      setBootstrapped(true);
    };

    bootstrap();
  }, []);

  return (
    <Placeholder loading={!bootstrapped}>
      {() => (
        <BrowserRouter>
          <Switch>
            <Route path="/gold" component={GoldApp} />
            <Route path="*" component={HeyDoctorApp} />
          </Switch>
        </BrowserRouter>
      )}
    </Placeholder>
  );
};

export const RouterWithOptimizely = withOptimizely(Router);
